import { usePlans } from "../contexts/PlansContext";
import {
  AUDIENCE,
  perkAudience,
  tierGrantsPerk,
  requiredTierForPerk,
  tierMeetsMinimum,
  getTierByKey,
} from "../constants/plans";

/**
 * usePerk — resolve whether the signed-in user's active tier grants a perk.
 *
 * Returns `{ granted, loading, activeTier, requiredTier }`:
 *   - granted:      boolean — does the user's tier unlock this perk?
 *   - loading:      boolean — plans/subscriptions still resolving (avoid
 *                   flashing an upgrade prompt before we know the tier)
 *   - activeTier:   the user's highest active tier for the perk's audience
 *   - requiredTier: the lowest tier that offers the perk (for prompts)
 *
 * IMPORTANT: this is a CLIENT-SIDE gate for UX, consistent with the existing
 * favorite-cap gating in Bookmarks. Paid tiers live in Wix and are not yet
 * visible to the backend, so this is NOT a hard security boundary. Features
 * whose data must be protected server-side need a Wix→Firestore membership sync
 * first (tracked in ROADMAP under "perk server enforcement").
 */
export function usePerk(perkId) {
  const plans = usePlans();
  const loading = !plans || plans.loading;
  const audience = perkAudience(perkId);
  const activeTier =
    audience === AUDIENCE.BUSINESS
      ? plans?.getActiveBusinessTier?.() ?? null
      : plans?.getActiveTravelerTier?.() ?? null;

  return {
    granted: tierGrantsPerk(activeTier, perkId),
    loading,
    activeTier,
    requiredTier: requiredTierForPerk(perkId),
  };
}

/**
 * useTierAccess — resolve whether the signed-in user's active traveler tier
 * meets a minimum tier (e.g. a members-only event's `minTier`). Same client-side
 * caveat as usePerk. Returns `{ granted, loading, activeTier, requiredTier }`.
 * A null/empty `minTierKey` is "no gate" → always granted.
 */
export function useTierAccess(minTierKey) {
  const plans = usePlans();
  const loading = !plans || plans.loading;
  const activeTier = plans?.getActiveTravelerTier?.() ?? null;
  return {
    granted: tierMeetsMinimum(activeTier, minTierKey),
    loading,
    activeTier,
    requiredTier: minTierKey ? getTierByKey(minTierKey) : null,
  };
}

export default usePerk;
